import React, { useState, useEffect } from "react";
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import moment from "moment";

import I18n from "../../../../base/i18n.js.jsx";

import HtmlContent from "../../../../base/components/html_content.js.jsx";
import Checkbox from "../../../../base/inputs/checkbox.js.jsx";
import Image from "../../../../base/components/image.js.jsx";
import Button from "../../../../base/buttons/button.js.jsx";

import FormFields from "../../../../base/form/form_fields.js.jsx";

import MoneyFormatter from "../../../../utils/money_formatter.js.jsx";
import PercentFormatter from "../../../../utils/percent_formatter.js.jsx";

import { ProjectDeveloperModel } from "../../../../models/projects/project_developer_model.js.jsx";
import { UserSignatureModel } from "../../../../models/users/user_signature_model.js.jsx";

const blockStyles = makeStyles(theme => ({
  root: {
  },
  summary: {
    display: "flex",
    marginTop: "40px",
    marginBottom: "56px",
    ['@media (max-width: 768px)']: {
      flexWrap: "wrap",
      marginTop: "8px",
      marginBottom: "24px",
    },
  },
  imageColumn: {
    flexBasis: "252px",
    height: "200px",
    flexShrink: 0,
    marginRight: "53px",
    ['@media (max-width: 768px)']: {
      marginRight: "0px",
      marginBottom: "20px",
      flexBasis: "100%",
      height: "178px",
    },
  },
  image: {
    width: "100%",
    height: "100%",
  },
  overviewInfoColumn: {
    flexGrow: "1"
  },
  projectTitle: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: "28px",
    paddingBottom: "14px",
    textTransform: "uppercase",
    marginTop: "-8px",
    ['@media (max-width: 768px)']: {
      fontSize: "20px",
    }
  },
  overviewTable: {

  },
  overviewRow: {
    display: "flex",
    "&:first-child $overviewColumn": {
      paddingTop: "0px"
    },
    "&:last-child $overviewColumn": {
      paddingBottom: "0px"
    },
  },
  overviewColumn: {
    fontSize: "18px",
    textAlign: "left",
    verticalAlign: "top",
    fontWeight: theme.typography.fontWeightLight,
    padding: "5px 0px",
    ['@media (max-width: 768px)']: {
      fontSize: "16px",
      lineHeight: "20px",
      padding: "4px 0px",
    },
  },
  labelColumn: {
  },
  valueColumn: {
    flexGrow: "1"
  },
  confirmations: {

  },
  confirmationRow: {

  },
  checkboxType: {
    margin: "0px 0px 28px",
    ['@media (max-width: 768px)']: {
      margin: "0px 0px 16px"
    }
  },
  textType: {
    margin: "48px 0px",
    ['@media (max-width: 768px)']: {
      margin: "24px 0px"
    }
  },
  infoText: {
    fontSize: "16px",
    lineHeight: "20px",
    fontWeight: theme.typography.fontWeightLight,
    color: theme.palette.black,
    ['@media (max-width: 768px)']: {
      fontSize: "12px",
      lineHeight: "18px",
    },
  },
  checkboxes: {
    listStyleType: "none",
    padding: "0px",
    "& li + li": {
      marginTop: "20px"
    },
    ['@media (max-width: 768px)']: {
      marginLeft: "-2px"
    },
  },
  checkbox: {
    alignItems: "start",
    "& .MuiCheckbox-root": {
      marginTop: "-9px",
      ['@media (max-width: 768px)']: {
        marginLeft: "4px",
        marginTop: "-7px"
      },
    },
    "& $infoText": {
      color: theme.palette.grayText,
    },
  },
  signature: {
    margin: "40px 0px",
    ['@media (max-width: 768px)']: {
      margin: "28px 0px 32px",
    },
  },
  desktop: {
    display: "block",
    ['@media (max-width: 768px)']: {
      display: "none"
    },
  },
  mobile: {
    display: "none",
    ['@media (max-width: 768px)']: {
      display: "block"
    },
  },
}));

const InvestmentConfirmationSection = (props) => {

  const classes = blockStyles();
  const { project } = props;
  const {
    name: projectName,
    developer: projectDeveloper,
    executionTimeFrom: projectExecutionTimeFrom,
    executionTimeTo: projectExecutionTimeTo,
    mainGalleryImage: { urls } = {}
  } = project;

  const { investment: { amount, signature: { date: signedAt } } } = props;
  const { onChangeConfirmationInfo, onChangeSignature } = props;

  const [confirmationInfo, setConfirmationInfo] = useState(props.confirmationInfo);
  const [signature, setSignature] = useState(props.signature);

  const [signatureErrors, setSignatureErrors] = useState(props.signatureErrors);

  let bonusRates = undefined
  let annualInterestRate = project.annualInterestRate

  if (project.bonusRates) {
  bonusRates = project.bonusRates.filter(br => moment(signedAt).isSameOrBefore(br.deadline, 'day') ).sort((br1, br2) => new Date(br1.deadline) >= new Date(br2.deadline))
    if (bonusRates.length > 0) {
      const bonusRate = bonusRates[0].rate;
      annualInterestRate += bonusRate
    }
  }

  useEffect(() => {
    setConfirmationInfo(props.confirmationInfo);
  }, [props.confirmationInfo]);

  useEffect(() => {
    setSignature(props.signature);
  }, [props.signature]);

  useEffect(() => {
    setSignatureErrors(props.signatureErrors);
  }, [props.signatureErrors]);

  const onChangeConfirmationInfoValue = (fieldName, value) => {
    const newInfo = confirmationInfo.map((info) => {
      if(info.fieldName === fieldName) {
        info.value = value;
      }
      return info;
    })
    setConfirmationInfo(newInfo);
    onChangeConfirmationInfo(newInfo);
  }

  const onItemChange = (changedItem) => {
    const newItem = {
      ...signature,
      ...changedItem
    };
    setSignature(newItem);
    onChangeSignature(newItem);
  }

  let src = undefined;
  let srcSet = {};
  if(urls) {
    src = urls.large;
    srcSet = {
      "2x": urls.large2x,
      "3x": urls.large3x
    }
  }

  const attributes = [
    {
      label: `${I18n.t("web.modal.submit_investment.confirmation.info.term")}: `,
      value: I18n.t("web.modal.submit_investment.confirmation.info.term_value", {
        count: projectExecutionTimeTo,
        from: projectExecutionTimeFrom,
        to: projectExecutionTimeTo
      }),
    },
    {
      label: `${I18n.t("web.modal.submit_investment.confirmation.info.annual_interest_rate")}: `,
      value: I18n.t("web.modal.submit_investment.confirmation.info.annual_interest_rate_value", {
        percents: PercentFormatter.format(annualInterestRate / 100)
      })
    },
    {
      label: `${I18n.t("web.modal.submit_investment.confirmation.info.investment_amount")}: `,
      value: `${MoneyFormatter.format(amount)} ${I18n.t("currency.euro")}`
    },
  ]

  if(projectDeveloper) {
    attributes.unshift({
      label: `${I18n.t("web.modal.submit_investment.confirmation.info.developer")}: `,
      value: ProjectDeveloperModel.displayValue(projectDeveloper)
    })
  }

  return (
    <div className = { classes.root } >
      <div className = { classes.summary} >
        <span className = { clsx(classes.projectTitle, classes.mobile)} > { projectName } </span>
        <div className = { classes.imageColumn } >
          <Image className = { classes.image }
                  alt = { projectName }
                  src = { src }
                  srcSet = { srcSet } />
        </div>
        <div className = { classes.overviewInfoColumn } >
          <div className = { clsx(classes.overviewTable) } >
            <div className = { classes.overviewRow } >
              <span className = { clsx(classes.projectTitle, classes.desktop) } > { projectName } </span>
            </div>
            {
              attributes.map((infoAttribute, index) => {
                const { label, value } = infoAttribute;
                return <div className = { classes.overviewRow }
                            key = { index } >
                  <div className = { clsx(classes.overviewColumn )} >{ `${label} ${value}`} </div>
                </div>
              })
            }
          </div>
        </div>
      </div>
      <div className = { classes.confirmations } >
        {
          confirmationInfo.map((info) => {
            const { fieldName, label, type, value } = info;
            return <div className = { clsx({
              [classes.confirmationRow]: true,
              [classes.checkboxType]: type === "checkbox",
              [classes.textType]: type === "text"
            }) }
                        key = { fieldName } >
              {
                type === "checkbox" &&
                <Checkbox className = { classes.checkbox }
                          checked = { value }
                          onChange = { (newValue) => onChangeConfirmationInfoValue(fieldName, newValue) }
                  displayName = {
                    <HtmlContent className = { classes.infoText } >
                      { label }
                    </HtmlContent>
                  } />
              }
              {
                type === "text" &&
                <HtmlContent className = { classes.infoText } >
                  { label }
                </HtmlContent>
              }
            </div>
          })
        }
        <div className = { clsx(classes.confirmationRow, classes.textType) } >
          <HtmlContent className = { classes.infoText } >
            { project.process == 'standard' ?
            I18n.t("web.modal.submit_investment.confirmation.signature_disclaimer") :
            I18n.t("web.modal.submit_investment.confirmation.small_project_signature_disclaimer") }
          </HtmlContent>
        </div>
        {project.process == 'standard' &&
          <div className={classes.signature} >
            <FormFields formFields={UserSignatureModel.formFields(signature)}
              item={signature}
              errorData={signatureErrors}
              onItemChange={onItemChange} />
          </div>}
      </div>
    </div>
  );
}

export default InvestmentConfirmationSection;